import React from 'react';

import { useLocation } from 'react-router-dom';

import { SidebarProvider } from '@/components/ui/sidebar';
import { RoutePermissionGuard } from '@/modules/authorization/components/route-permission-guard';
import { AppSidebar } from '@/modules/common';
import { useFeatureFlags } from '@/modules/common/hooks';
import { TrialEndedScreen, useDemoStatus } from '@/modules/demo';
import {
  NotificationDeniedModal,
  NotificationPermissionModal,
} from '@/modules/notifications/components';
import {
  useNotificationPermission,
  useNotificationTrayRefresh,
  useSilentQueryInvalidation,
  useToastNotifications,
  useVisibleNotifications,
} from '@/modules/notifications/hooks';
import { AuthenticatedRoutes } from '@/router/routes';
import { RootStates } from '@/router/state/root/component/root-states';
import { useRootSelector } from '@/router/state/root/util/context/helpers';

function NotificationListeners() {
  const {
    showPermissionModal,
    showDeniedModal,
    handleEnableNotifications,
    handleDismissPermissionModal,
    handleDismissDeniedModal,
  } = useNotificationPermission();

  useVisibleNotifications();
  useSilentQueryInvalidation();
  useNotificationTrayRefresh();
  useToastNotifications();

  return (
    <>
      <NotificationPermissionModal
        open={showPermissionModal}
        onEnable={handleEnableNotifications}
        onDismiss={handleDismissPermissionModal}
      />
      <NotificationDeniedModal open={showDeniedModal} onDismiss={handleDismissDeniedModal} />
    </>
  );
}

export const AuthenticatedTemplate = ({ children }: { children: React.ReactNode }) => {
  const { isDemoEnv, showNotifications } = useFeatureFlags();
  const { daysLeft } = useDemoStatus();
  const location = useLocation();

  // The notification permission modal must only appear in the fully-authenticated
  // ("main") flow — never during onboarding. The OAuth callback route lives under
  // the authenticated template too, so without this gate the modal flashes when the
  // user returns from Jira OAuth mid-onboarding (OPS-1427).
  const isMainAuthenticated = useRootSelector(state =>
    state.matches({ [RootStates.Authenticated]: 'main' })
  );

  // Defense-in-depth: never run the notification logic on the OAuth callback
  // route. It's part of the onboarding integration flow even though it's
  // technically an authenticated route (OPS-1427).
  const isOAuthCallbackRoute =
    location.pathname === AuthenticatedRoutes.OnboardingIntegrationsOAuthCallback;

  const shouldMountNotifications =
    showNotifications && isMainAuthenticated && !isOAuthCallbackRoute;

  const isTrialEnded = isDemoEnv && daysLeft === 0;

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {isTrialEnded ? (
        <TrialEndedScreen />
      ) : (
        <RoutePermissionGuard>
          <SidebarProvider className="min-h-0 max-w-[100vw] flex-1 overflow-hidden">
            <AppSidebar />
            <main className="h-full max-w-full flex-1 overflow-hidden">{children}</main>
          </SidebarProvider>
        </RoutePermissionGuard>
      )}

      {shouldMountNotifications && <NotificationListeners />}
    </div>
  );
};
